import { useMemo, useState } from "react";
import { useSettings } from "./useSettings";
import { useT } from "../i18n/useT";
import { clearDuCacheForCurrentTerm } from "../calendar/group/duClient";

function clamp4Digits(v: string) {
  const digits = (v || "").replace(/\D/g, "").slice(0, 4);
  return digits;
}

function buildGroup(prefix: string, num4: string, isMaster: boolean) {
  const p = (prefix || "").trim();
  const n = clamp4Digits(num4);
  if (!p || n.length !== 4) return "";
  return `${p}-${n}${isMaster ? "M" : ""}`;
}

export function GroupPickerCard() {
  const t = useT();
  const [s, setS] = useSettings();

  const current = useMemo(() => (s.selectedGroup || "").trim(), [s.selectedGroup]);

  // Try to prefill from saved value, e.g. "SE-2417M"
  const pre = useMemo(() => {
    const m = current.match(/^([A-Za-z]{2,})-(\d{4})(M)?$/);
    return {
      prefix: m?.[1]?.toUpperCase() || "SE",
      num: m?.[2] || "",
      master: Boolean(m?.[3])
    };
  }, [current]);

  const [prefix, setPrefix] = useState(pre.prefix);
  const [num, setNum] = useState(pre.num);
  const [isMaster, setIsMaster] = useState(pre.master);

  const built = useMemo(() => buildGroup(prefix, num, isMaster), [prefix, num, isMaster]);
  const canSaveBuilt = built.length > 0;

  const saveGroup = (g: string) => {
    const x = (g || "").trim();
    if (!x) return;
    setS({ selectedGroup: x, scheduleSource: "group" });
  };

  return (
    <div className="settings-card">
      <h3>{t("settings.group")}</h3>

      <div className="block">
        <div className="row wrap">
          <button
            className={`btn ${s.scheduleSource === "group" ? "primary" : "ghost"}`}
            onClick={() => setS({ scheduleSource: "group" })}
          >
            {t("settings.group.source.groupApi")}
          </button>
          <button
            className={`btn ${s.scheduleSource === "ics" ? "primary" : "ghost"}`}
            onClick={() => setS({ scheduleSource: "ics" })}
          >
            {t("settings.group.source.ics")}
          </button>
        </div>
      </div>

      <div className="block">
        <div className="muted small">
          {t("settings.group.selected")}: <b>{current || "-"}</b>
        </div>
        <div className="row wrap" style={{ marginTop: 10 }}>
          <button
            className="btn ghost"
            disabled={!current}
            onClick={() => {
              clearDuCacheForCurrentTerm(current || undefined);
              window.dispatchEvent(new Event("du:refresh"));
            }}
          >
            {t("settings.group.refreshDu")}
          </button>
        </div>
      </div>

      <div className="block">
        <div className="muted small">{t("settings.group.builder")}</div>
        <div className="row wrap" style={{ gap: 10 }}>
          <input
            className="input group-prefix-input"
            value={prefix}
            onChange={(e) => setPrefix(e.target.value.replace(/[^A-Za-z]/g, "").slice(0, 8).toUpperCase())}
            placeholder={t("settings.group.prefix")}
            style={{ width: 140 }}
          />

          <span className="muted" style={{ alignSelf: "center" }}>
            -
          </span>

          <input
            className="input"
            value={num}
            onChange={(e) => setNum(clamp4Digits(e.target.value))}
            placeholder={t("settings.group.number")}
            inputMode="numeric"
            style={{ width: 140 }}
          />

          <label className="row" style={{ gap: 8, alignItems: "center" }}>
            <input type="checkbox" checked={isMaster} onChange={(e) => setIsMaster(e.target.checked)} />
            <span className="muted small">M</span>
          </label>
        </div>

        <div className="row wrap" style={{ marginTop: 10 }}>
          <button className={`btn primary ${!canSaveBuilt ? "is-disabled" : ""}`} disabled={!canSaveBuilt} onClick={() => saveGroup(built)}>
            {t("common.save")}
          </button>
        </div>
      </div>
    </div>
  );
}
